import dayjs from 'dayjs'
import { useChatbotAttachmentsOptional } from 'features/chatbot'
import { useTimeSeriesSensorsListener } from 'features/knowledgeDocumentViewer/contexts/TimeSeriesSensorsListenerContext'
import { GeckoNotificationProvider } from 'features/notifications'
import { generateEntrySlug } from 'features/rolodex'
import {
  ActiveTimeRangeSelector,
  PenDrawer,
  ScatterplotProvider,
  ScatterplotSidebar,
  ScatterplotSidebarView,
  TimeSeriesScatterplotContent,
  TimeSeriesService,
  useExclusivePanels,
  useScatterplotContext,
} from 'features/timeSeries'
import { CHART_COLORS } from 'features/timeSeries/config/constants'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { RolodexEntryType } from 'types'

const CHART_BACKGROUND = '#0a0a0a'

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
  background: ${CHART_BACKGROUND};
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
`

const ChartWithFooterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

const ChartFooter = styled.div`
  padding: 0.25rem 0.375rem;
  border-top: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  background: ${CHART_COLORS.BACKGROUND};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`

/** What a completed save inside the embedded chart looked like. */
export interface EmbeddedChartSavedInfo {
  chartID: string
  isNewChart: boolean
  /** Chart name at save time ('' when the user left it blank). */
  name: string
  /** Sensor ids plotted at save time. */
  sensorIds: string[]
}

interface EmbeddedTimeSeriesChartProps {
  sensorIds: string[]
  siteID: string
  /**
   * Stable id of the doc-viewer tab hosting this chart. Used as the local-state
   * persistence scope so each tab restores its own pens + time range after a
   * browser refresh (the tab id survives refresh via the sessionStorage tab
   * state; the chart's own transient slug regenerates every mount and cannot).
   */
  tabId: string
  initialTimeRange?: { from: string; to: string }
  autoOpenTagSearch?: boolean
  /** Per-sensor line colors. */
  sensorColors?: Record<string, string>
  /** Sensor ids to render de-emphasized (dimmed/dotted). */
  deemphasizedSensorIds?: string[]
  /**
   * When true, `sensorIds` is a controlled set: the chart selection is replaced
   * to match exactly on every change (add + remove) instead of additively seeded.
   */
  controlled?: boolean
  /**
   * Id of a SAVED chart to render with full fidelity: the provider resolves it
   * and applies the saved config (pens, colors, y-axis modes, date config),
   * and Save updates that chart in place. Read once at mount — a tab upgraded
   * in place after its first save keeps its already-promoted transient
   * provider (same id) without prop churn. When set, the sensor seeding props
   * (`sensorIds`, `sensorColors`, `deemphasizedSensorIds`, `controlled`,
   * `initialTimeRange`) are ignored so they cannot fight the resolved config.
   */
  savedChartID?: string
  /**
   * Pre-fills the name prompt shown on a new chart's first save so the default
   * matches the doc-viewer tab this chart lives in. Ignored for saved-chart
   * tabs, whose name comes from the resolved chart. Empty/undefined falls back
   * to the provider's own default (blank).
   */
  defaultChartName?: string
  /** Fires after any successful save (first promotion or later updates). */
  onChartSaved?: (info: EmbeddedChartSavedInfo) => void
}

interface SaveMeta {
  name: string
  sensorIds: string[]
}

interface DashboardContentProps {
  autoOpenTagSearch?: boolean
  /**
   * Kept current (every render) with the state a save would persist, so the
   * provider-level onChartSaved callback can report name/sensors without
   * re-plumbing the save path.
   */
  saveMetaRef?: React.MutableRefObject<SaveMeta>
  /**
   * Saved-chart tabs skip the one-shot post-load time-range reset below: the
   * resolved chart applies its own date config, and re-setting the range to a
   * 'custom' preset would stomp it.
   */
  isSavedChart?: boolean
}

const TIME_WINDOW_ATTACHMENT_ID = 'active-time-window'

function formatTimeRange(from: string, to: string): string {
  const fromDate = dayjs(from)
  const toDate = dayjs(to)
  return `${fromDate.format('MMM D, HH:mm')} - ${toDate.format('MMM D, HH:mm')}`
}

const DashboardContent: React.FC<DashboardContentProps> = ({
  autoOpenTagSearch = false,
  saveMetaRef,
  isSavedChart = false,
}) => {
  const {
    sensorEntries,
    selectedSensors,
    timeRange,
    setTimeRange,
    isLoading,
    liveTail,
    resetViewport,
    chartName,
  } = useScatterplotContext()

  if (saveMetaRef) {
    saveMetaRef.current = {
      name: chartName,
      sensorIds: selectedSensors.map((s) => s.id),
    }
  }
  const chatbotAttachments = useChatbotAttachmentsOptional()
  const chatbotAttachmentsRef = useRef(chatbotAttachments)
  chatbotAttachmentsRef.current = chatbotAttachments

  // Report the chart's actual selected sensors to any embedder listener so
  // sensors added via the chart's own tag-search / pen drawer (which never reach
  // the tab's `sensorIds` history item) can still be observed/logged.
  const sensorsListener = useTimeSeriesSensorsListener()
  const sensorsListenerRef = useRef(sensorsListener)
  sensorsListenerRef.current = sensorsListener
  const selectedSensorKey = selectedSensors.map((s) => s.id).join(',')
  useEffect(() => {
    if (!selectedSensorKey) return
    sensorsListenerRef.current?.(selectedSensorKey.split(','))
  }, [selectedSensorKey])
  const prevTimeRangeRef = useRef<{ from: string; to: string } | null>(null)
  const wasLoadingRef = useRef(false)
  const hasInitialResetRef = useRef(false)
  const timeRangeRef = useRef(timeRange)
  timeRangeRef.current = timeRange

  // After the first data load completes (isLoading transitions true→false),
  // re-set the time range with computed interval so the chart is recreated
  // with correct Y-axis scales from the loaded data.
  useEffect(() => {
    if (isSavedChart) return
    if (isLoading && selectedSensors.length > 0) {
      wasLoadingRef.current = true
    }
    if (wasLoadingRef.current && !isLoading && !hasInitialResetRef.current) {
      hasInitialResetRef.current = true
      const tr = timeRangeRef.current
      const from = dayjs(tr.from)
      const to = dayjs(tr.to)
      const rangeSeconds = to.diff(from, 'seconds')
      const { interval, interval_unit } =
        TimeSeriesService.getInterval(rangeSeconds)
      setTimeRange({
        from: from.toISOString(),
        to: to.toISOString(),
        preset: 'custom',
        interval,
        intervalUnit: interval_unit,
      })
    }
  }, [isSavedChart, isLoading, selectedSensors.length, setTimeRange])

  const {
    leftOpen: sidebarOpen,
    rightOpen: penDrawerOpen,
    toggleLeft: handleSettingsClick,
    closeLeft: handleSidebarClose,
    toggleRight: handlePenDrawerClick,
  } = useExclusivePanels(autoOpenTagSearch, !autoOpenTagSearch)
  const [sidebarView, setSidebarView] = useState<ScatterplotSidebarView>(
    autoOpenTagSearch
      ? ScatterplotSidebarView.ADD
      : ScatterplotSidebarView.SETTINGS
  )

  useEffect(() => {
    const chatbot = chatbotAttachmentsRef.current
    if (!chatbot) return

    const { from, to } = timeRange
    const prev = prevTimeRangeRef.current

    if (prev && prev.from === from && prev.to === to) {
      return
    }

    prevTimeRangeRef.current = { from, to }

    chatbot.removeAttachment(TIME_WINDOW_ATTACHMENT_ID)
    chatbot.addAttachment({
      id: TIME_WINDOW_ATTACHMENT_ID,
      type: 'timeWindow',
      name: 'Time Range',
      displayTitle: formatTimeRange(from, to),
      timeWindow: { from, to },
    })
  }, [timeRange])

  useEffect(() => {
    return () => {
      const chatbot = chatbotAttachmentsRef.current
      if (chatbot) {
        chatbot.removeAttachment(TIME_WINDOW_ATTACHMENT_ID)
      }
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 100)
    return () => clearTimeout(timer)
  }, [sidebarView, sidebarOpen, penDrawerOpen])

  return (
    <DashboardContainer>
      <ContentContainer>
        <ChartWithFooterWrapper>
          <TimeSeriesScatterplotContent
            showChartSettings={false}
            hideTooltip={penDrawerOpen}
          />
          <ChartFooter>
            <ActiveTimeRangeSelector
              timeRange={timeRange}
              setTimeRange={setTimeRange}
              liveTail={liveTail}
              onSettingsClick={handleSettingsClick}
              onResetViewport={resetViewport}
              onPenDrawerClick={handlePenDrawerClick}
              isSettingsOpen={sidebarOpen}
              isPenDrawerOpen={penDrawerOpen}
            />
          </ChartFooter>
        </ChartWithFooterWrapper>
        {sidebarOpen && (
          <ScatterplotSidebar
            onClose={handleSidebarClose}
            tagsOptions={sensorEntries}
            selectedTags={selectedSensors}
            setSidebarView={setSidebarView}
            sidebarView={sidebarView}
          />
        )}
        {penDrawerOpen && <PenDrawer />}
      </ContentContainer>
    </DashboardContainer>
  )
}

export const EmbeddedTimeSeriesChart: React.FC<
  EmbeddedTimeSeriesChartProps
> = ({
  sensorIds,
  siteID,
  tabId,
  initialTimeRange,
  autoOpenTagSearch,
  sensorColors,
  deemphasizedSensorIds,
  controlled,
  savedChartID,
  defaultChartName,
  onChartSaved,
}) => {
  // Mode is fixed at mount. A sensor-seeded tab whose history item is later
  // upgraded with the chart id minted by its first save must NOT flip modes:
  // its provider already promoted the transient slug (same id) and keeps
  // saving to it; flipping `transient`/seed props mid-flight would only churn
  // the mounted chart. Tabs that mount with `savedChartID` (Recent Chart card,
  // restored tabs) render the saved chart via the resolve/apply flow.
  const [mountSavedChartID] = useState(() => savedChartID)
  const isSavedChart = Boolean(mountSavedChartID)
  const ephemeralChartID = useMemo(
    () => generateEntrySlug(RolodexEntryType.CHART),
    []
  )
  const chartID = mountSavedChartID ?? ephemeralChartID

  const saveMetaRef = useRef<SaveMeta>({ name: '', sensorIds: [] })
  const onChartSavedRef = useRef(onChartSaved)
  onChartSavedRef.current = onChartSaved
  const handleChartSaved = useCallback(
    async (savedID: string, isNewChart: boolean) => {
      onChartSavedRef.current?.({
        chartID: savedID,
        isNewChart,
        ...saveMetaRef.current,
      })
    },
    []
  )

  return (
    <GeckoNotificationProvider>
      <ScatterplotProvider
        chartID={chartID}
        siteID={siteID}
        // Saved-chart tabs resolve the real chart (full fidelity) and Save
        // updates it. Sensor-seeded tabs are transient rather than
        // disableSaving: the ephemeral slug is never resolved (no 404 toast),
        // but Save still works — the first save promotes it into a real chart
        // entry, same as the ad-hoc page. The embedded tab stays put after
        // saving; the chart just appears on the charts page.
        transient={!isSavedChart}
        // Persist this tab's live state (pens + time range + view) to
        // localStorage, keyed by the STABLE TAB ID rather than the chart id or
        // a shared `adhoc` scope. Per-tab keying is what makes this safe where
        // the original code punted: (1) keep-alive keeps a tab's provider
        // mounted across tab switches, so hydration only ever runs on a real
        // remount (page refresh) — there is no in-memory keep-alive state for
        // it to fight; (2) the tab id is unique per tab, so multiple open tabs
        // (and two tabs on the same saved chart) never clobber one shared key.
        // Skipped for `controlled` tabs (the claims pinned tab): their
        // selection is driven externally via overrideSensorIds and must not be
        // overridden by a persisted snapshot.
        persistLocalState={!controlled}
        localStateScope={tabId}
        // Seed the editable name from the tab so the first-save name prompt
        // defaults to the tab name. Saved-chart tabs take their name from the
        // resolved chart instead.
        initialChartName={isSavedChart ? undefined : defaultChartName}
        onChartSaved={handleChartSaved}
        // Seeding props would fight the resolved chart config, so saved-chart
        // tabs pass none of them.
        // Controlled: drive the exact selection via `overrideSensorIds` (replaces
        // on every change). Default: additively seed via `initialSensorIds`.
        {...(isSavedChart
          ? {}
          : controlled
            ? {
                overrideSensorIds: sensorIds,
                // Keep pens the user adds via the chart's own tag-search when the
                // page-controlled set later changes (instead of wiping them).
                preserveManualSensors: true,
              }
            : { initialSensorIds: sensorIds })}
        initialSensorColors={isSavedChart ? undefined : sensorColors}
        deemphasizedSensorIds={isSavedChart ? undefined : deemphasizedSensorIds}
        initialTimeRange={isSavedChart ? undefined : initialTimeRange}>
        <DashboardContent
          autoOpenTagSearch={autoOpenTagSearch}
          saveMetaRef={saveMetaRef}
          isSavedChart={isSavedChart}
        />
      </ScatterplotProvider>
    </GeckoNotificationProvider>
  )
}
